import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold } from '../../lib/fonts';
import { useLingui } from '@lingui/react';
import { type LocalizedMessage, resolveMessage } from '../../lib/i18n/messages';

export type ProjectFeedFilterPillTone =
    | 'default'
    | 'must-read'
    | 'related'
    | 'liked'
    | 'starred'
    | 'disliked';

export interface ProjectFeedFilterPillOption<T extends string> {
    value: T;
    label: LocalizedMessage;
    icon?: 'star' | 'link' | 'thumbs-up' | 'thumbs-down';
    tone: ProjectFeedFilterPillTone;
}

interface ProjectFeedFilterPillsProps<T extends string> {
    label?: LocalizedMessage;
    labelWidth?: number;
    options: ProjectFeedFilterPillOption<T>[];
    selectedValue: T;
    onChange: (value: T) => void;
    clearValue?: T;
    toggleToClear?: boolean;
    compact?: boolean;
    stacked?: boolean;
}

function getChipState(tone: ProjectFeedFilterPillTone, isActive: boolean) {
    if (!isActive) {
        return {
            container: styles.chip,
            text: styles.chipText,
            iconColor: colors.text600,
            iconSolid: false,
        };
    }

    return {
        container: [styles.chip, styles.chipActive],
        text: [styles.chipText, styles.chipTextActive],
        iconColor: colors.primaryGreenDark,
        iconSolid: true,
    };
}

export default function ProjectFeedFilterPills<T extends string>({
    label,
    labelWidth,
    options,
    selectedValue,
    onChange,
    clearValue,
    toggleToClear = false,
    compact = false,
    stacked = false,
}: ProjectFeedFilterPillsProps<T>) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();

    return (
        <View style={[styles.group, stacked && styles.groupStacked]}>
            {label ? (
                <Text
                    style={[
                        styles.label,
                        !stacked && labelWidth ? { width: labelWidth } : null,
                        compact && styles.labelCompact,
                        stacked && styles.labelStacked,
                        fontsLoaded && interSemiBold,
                    ]}
                >
                    {resolveMessage(i18n, label)}
                </Text>
            ) : null}
            <View style={[styles.row, stacked && styles.rowStacked]}>
                {options.map((option) => {
                    const isActive = selectedValue === option.value;
                    const chipState = getChipState(option.tone, isActive);

                    return (
                        <TouchableOpacity
                            key={option.value}
                            style={[chipState.container, compact && styles.chipCompact]}
                            onPress={() => onChange(toggleToClear && isActive && clearValue ? clearValue : option.value)}
                            activeOpacity={0.7}
                            testID={`project-feed-filter-${option.value}`}
                        >
                            {option.icon ? (
                                <FontAwesome6
                                    name={option.icon}
                                    size={compact ? 12 : 11}
                                    color={chipState.iconColor}
                                    solid={option.icon === 'star' && chipState.iconSolid}
                                />
                            ) : null}
                            <Text style={[chipState.text, compact && styles.chipTextCompact, fontsLoaded && interSemiBold]}>
                                {resolveMessage(i18n, option.label)}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    group: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 1,
    },
    groupStacked: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
    },
    label: {
        fontSize: 11,
        lineHeight: 14,
        color: colors.text500,
        fontWeight: '600',
        letterSpacing: 0.2,
        textTransform: 'uppercase',
    },
    labelCompact: {
        fontSize: 10,
        lineHeight: 12,
    },
    labelStacked: {
        width: 'auto',
    },
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
    },
    rowStacked: {
        width: '100%',
        flex: 0,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#d8dde6',
    },
    chipActive: {
        backgroundColor: '#f4f8f0',
        borderColor: '#c3d3bc',
    },
    chipCompact: {
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    chipText: {
        fontSize: 12,
        color: colors.text700,
    },
    chipTextCompact: {
        fontSize: 12,
    },
    chipTextActive: {
        color: colors.primaryGreenDark,
    },
});
